import {z} from 'zod';

// Display order of the Photo Type codelist, grouped as in T20-138662.
const EXTERIOR_PHOTO_TYPES = [
  'FRONT_LEFT',
  'FRONT',
  'FRONT_RIGHT',
  'RIGHT_SIDE',
  'REAR_RIGHT',
  'REAR',
  'REAR_LEFT',
  'LEFT_SIDE',
  'WINDSHIELD',
  'FRONT_LIGHT',
  'TAILLIGHT',
  'ROOF',
  'LEFT_MIRROR',
  'LEFT_FRONT_DOOR_HANDLE',
  'WHEEL',
  'ENGINE',
  'FUEL_CHARGING_PORT',
  'EXTERIOR_DETAIL',
] as const;

const INTERIOR_PHOTO_TYPES = [
  'DASHBOARD',
  'INSTRUMENTS_PANEL',
  'STEERING_WHEEL',
  'INFOTAINMENT',
  'FRONT_SEATS',
  'BACK_SEATS',
  'TRUNK',
  'SHIFT_LEVER',
  'LIGHT_ASSISTANT_PANEL',
  'DRIVER_DOOR',
  'ELECTRIC_CONTROLS',
  'INTERIOR_DETAIL',
] as const;

const OTHER_PHOTO_TYPES = ['VIN_LABEL', 'GENERAL_ADVERTISING', 'UNCLASSIFIED'] as const;

/** What a sale vehicle photo shows; the identity of a Photo Guide slot. */
export const SaleVehiclePhotoTypeSchema = z.enum([
  ...EXTERIOR_PHOTO_TYPES,
  ...INTERIOR_PHOTO_TYPES,
  ...OTHER_PHOTO_TYPES,
]);
export type SaleVehiclePhotoType = z.infer<typeof SaleVehiclePhotoTypeSchema>;

export const SaleVehiclePhotoTypeGroupSchema = z.enum(['EXTERIOR', 'INTERIOR', 'OTHER']);
export type SaleVehiclePhotoTypeGroup = z.infer<typeof SaleVehiclePhotoTypeGroupSchema>;

/** Every Photo Type of each group, in display order. */
export const SALE_VEHICLE_PHOTO_TYPES_BY_GROUP = {
  EXTERIOR: EXTERIOR_PHOTO_TYPES,
  INTERIOR: INTERIOR_PHOTO_TYPES,
  OTHER: OTHER_PHOTO_TYPES,
} satisfies Record<SaleVehiclePhotoTypeGroup, readonly SaleVehiclePhotoType[]>;

/**
 * The 29 Photo Types a Photo Guide slot may use: the ones with a CarAudit photo position. The four
 * excluded types can be assigned to a photo but never form a slot.
 */
export const SlotSaleVehiclePhotoTypeSchema = SaleVehiclePhotoTypeSchema.exclude([
  'EXTERIOR_DETAIL',
  'INTERIOR_DETAIL',
  'GENERAL_ADVERTISING',
  'UNCLASSIFIED',
]);
export type SlotSaleVehiclePhotoType = z.infer<typeof SlotSaleVehiclePhotoTypeSchema>;

export const PhotoGuideSlotSchema = z.object({
  photoType: SlotSaleVehiclePhotoTypeSchema,
  required: z.boolean(),
});
export type PhotoGuideSlot = z.infer<typeof PhotoGuideSlotSchema>;

/** The tenant's Photo Guide: whether it is on and its ordered slots. */
export const PhotoGuideSettingSchema = z.object({
  enabled: z.boolean(),
  slots: z.array(PhotoGuideSlotSchema),
});
export type PhotoGuideSetting = z.infer<typeof PhotoGuideSettingSchema>;
